import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Stack, Chip, LinearProgress,
} from "@mui/material";
import {
  School, Send, ArrowForward, CheckCircle, EmojiObjects, WhatsApp, ContentCopy, Check, Share,
} from "@mui/icons-material";

const DR_PAW_WHATSAPP = "96170886078";
const DOMAIN          = "https://drpawleb.com";
const DISPLAY         = '"Cormorant Garamond", Georgia, serif';
const BLUE            = "#1565C0";
const BABY            = "#42A5F5";

const DrPawLogo: React.FC = () => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "12px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.1rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>
      Dr.Paw
    </Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
const PublicQA: React.FC = () => {
  const [searchParams] = useSearchParams();
  const question = decodeURIComponent(searchParams.get("q") || "");
  const link     = searchParams.get("link") || `${DOMAIN}/#pricing`;

  const [name,      setName]      = useState("");
  const [answer,    setAnswer]    = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [copied,    setCopied]    = useState(false);

  const socialCount = useMemo(() => {
    const hash = question.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return 320 + (hash % 620);
  }, [question]);

  const handleSubmit = () => {
    if (!answer.trim()) return;
    setSubmitted(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleNativeShare = async () => {
    const data = {
      title: "Dr.Paw Vet Question",
      text: `Can you answer this vet question? 🐾\n\n"${question}"\n\nTest yourself for free:`,
      url: window.location.href,
    };
    if (typeof navigator.share === "function") {
      try { await navigator.share(data); return; } catch {}
    }
    handleCopy();
  };

  const challengeMsg = encodeURIComponent(
    `Can you answer this vet question? 🐾\n\n"${question}"\n\nTest yourself: ${window.location.href}`
  );
  const challengeWaUrl = `https://wa.me/?text=${challengeMsg}`;

  const waMsg = encodeURIComponent(
    `Hi Dr.Paw! I just answered this question on your platform:\n\n"${question}"\n\nMy answer: ${answer}${name ? `\n\nI'm ${name}.` : ""}\n\nI'd love to learn more about your courses!`
  );
  const waUrl = `https://wa.me/${DR_PAW_WHATSAPP}?text=${waMsg}`;

  if (!question) {
    return (
      <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#90A4AE" }}>Invalid or missing question link.</Typography>
      </Box>
    );
  }

  const metaTitle = question ? `Q&A: ${question.slice(0, 60)} — Dr.Paw` : "Vet Q&A — Dr.Paw";
  const metaDesc  = question
    ? `"${question}" — Share your answer and learn from the Dr.Paw veterinary community.`
    : "Join the Dr.Paw veterinary Q&A community. Share your knowledge and learn from fellow vets.";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      {/* Blobs */}
      <Box sx={{ position: "absolute", top: -120, right: -120, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 3, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: BABY, fontWeight: 600, fontSize: ".72rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Veterinary Excellence Platform
        </Typography>
      </Box>

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 580 }}>

          {!submitted ? (
            <>
              {/* Question card */}
              <Box sx={{
                background: "#fff",
                border: "1px solid rgba(21,101,192,.15)",
                borderRadius: "24px",
                boxShadow: "0 4px 24px rgba(21,101,192,.07)",
                p: { xs: 3, md: 4 },
                mb: 2.5,
                position: "relative",
              }}>
                <Box sx={{ position: "absolute", left: 0, top: "25%", width: 4, height: "50%", background: `linear-gradient(180deg,transparent,${BLUE},transparent)`, borderRadius: "0 4px 4px 0" }} />

                <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mb: 2.5 }}>
                  <Chip label="Question For You" size="small" sx={{ bgcolor: "rgba(21,101,192,.08)", color: BLUE, fontWeight: 700, fontSize: ".7rem" }} />
                  <Chip
                    icon={<EmojiObjects sx={{ fontSize: ".9rem !important", color: "#D97706 !important" }} />}
                    label="Share your thoughts"
                    size="small"
                    sx={{ bgcolor: "rgba(217,119,6,.08)", color: "#D97706", fontWeight: 600, fontSize: ".7rem" }}
                  />
                  <Chip label={`${socialCount.toLocaleString()}+ vets answered`} size="small" sx={{ bgcolor: "rgba(34,197,94,.08)", color: "#059669", fontWeight: 600, fontSize: ".7rem" }} />
                </Stack>

                <Typography sx={{
                  color: "#0A1628", fontWeight: 700,
                  fontSize: { xs: "1.4rem", md: "1.75rem" },
                  lineHeight: 1.3, letterSpacing: "-.01em", mb: 2,
                  fontFamily: DISPLAY,
                }}>
                  {question}
                </Typography>

                <Typography sx={{ color: "#546E7A", fontSize: ".88rem", lineHeight: 1.75 }}>
                  Great vets never stop asking questions. Share your perspective — every answer is a step forward in your journey.
                </Typography>
              </Box>

              {/* Answer form */}
              <Box sx={{
                background: "#fff",
                border: "1px solid rgba(21,101,192,.12)",
                borderRadius: "20px",
                boxShadow: "0 2px 12px rgba(21,101,192,.05)",
                p: { xs: 2.5, md: 3 },
              }}>
                <TextField
                  label="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth size="small"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": { color: "#37474F", borderRadius: "10px", "& fieldset": { borderColor: "rgba(21,101,192,.2)" }, "&:hover fieldset": { borderColor: BLUE }, "&.Mui-focused fieldset": { borderColor: BLUE } },
                    "& .MuiInputLabel-root": { color: "#90A4AE" },
                    "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
                  }}
                />
                <TextField
                  label="Write your answer..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  multiline rows={5} fullWidth
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": { color: "#37474F", borderRadius: "14px", "& fieldset": { borderColor: "rgba(21,101,192,.2)" }, "&:hover fieldset": { borderColor: BLUE }, "&.Mui-focused fieldset": { borderColor: BLUE } },
                    "& .MuiInputLabel-root": { color: "#90A4AE" },
                    "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
                  }}
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2.5 }}>
                  <Typography variant="caption" sx={{ color: "#90A4AE", fontSize: ".75rem" }}>
                    {answer.length} characters
                  </Typography>
                </Stack>

                <Button
                  fullWidth variant="contained"
                  endIcon={<Send />}
                  onClick={handleSubmit}
                  disabled={!answer.trim()}
                  sx={{
                    py: 1.6, borderRadius: "14px", textTransform: "none",
                    fontWeight: 800, fontSize: "1rem",
                    background: "linear-gradient(135deg,#1565C0,#42A5F5)",
                    boxShadow: "0 6px 20px rgba(21,101,192,.3)",
                    "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", boxShadow: "0 8px 28px rgba(21,101,192,.4)", transform: "translateY(-1px)" },
                    "&.Mui-disabled": { opacity: .35, color: "#fff", background: "rgba(21,101,192,.3)" },
                    transition: "all .2s ease",
                  }}
                >
                  Submit My Answer
                </Button>
              </Box>
            </>
          ) : (
            /* Thank-you screen */
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{
                width: 90, height: 90, borderRadius: "50%", mx: "auto", mb: 3,
                background: "radial-gradient(circle,rgba(34,197,94,.15) 0%,transparent 70%)",
                border: "2px solid rgba(34,197,94,.35)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <CheckCircle sx={{ color: "#22C55E", fontSize: 44 }} />
              </Box>

              <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.9rem", md: "2.5rem" }, letterSpacing: "-.01em", mb: 1, fontFamily: DISPLAY }}>
                {name ? `Thank you, ${name}! 🐾` : "Thank you! 🐾"}
              </Typography>
              <Typography sx={{ color: "#546E7A", fontSize: ".95rem", lineHeight: 1.8, maxWidth: 420, mx: "auto", mb: 4.5 }}>
                Your curiosity is what separates good vets from great ones. Never stop questioning, never stop learning — that is the Dr.Paw spirit.
              </Typography>

              {/* CTA card */}
              <Box sx={{
                background: "#fff",
                border: "1px solid rgba(21,101,192,.15)",
                borderRadius: "24px",
                boxShadow: "0 4px 20px rgba(21,101,192,.07)",
                p: { xs: 3, md: 4 },
                mb: 2.5, textAlign: "left",
              }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg,#1565C0,#42A5F5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <School sx={{ color: "#fff", fontSize: "1.3rem" }} />
                  </Box>
                  <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.05rem" }}>
                    Want to go deeper?
                  </Typography>
                </Stack>
                <Typography sx={{ color: "#546E7A", fontSize: ".88rem", lineHeight: 1.75, mb: 1 }}>
                  Dr.Paw has expert-led courses designed to answer exactly these questions — and hundreds more.
                </Typography>
                <LinearProgress variant="determinate" value={75} sx={{ borderRadius: 99, height: 5, mb: 1.5, bgcolor: "rgba(21,101,192,.1)", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#1565C0,#42A5F5)", borderRadius: 99 } }} />
                <Typography variant="caption" sx={{ color: "#90A4AE", fontSize: ".75rem", display: "block", mb: 2.5 }}>
                  Join 1,000+ vets already learning on Dr.Paw
                </Typography>

                <Button
                  variant="contained" endIcon={<ArrowForward />} href={link} fullWidth
                  sx={{ py: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 800, fontSize: ".95rem", background: "linear-gradient(135deg,#1565C0,#42A5F5)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-2px)" }, transition: "all .2s ease", mb: 1.5 }}
                >
                  Explore Dr.Paw Courses
                </Button>

                <Button
                  variant="outlined" href={waUrl} target="_blank" startIcon={<WhatsApp />} fullWidth
                  sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" } }}
                >
                  Send answer to Dr.Paw on WhatsApp
                </Button>
              </Box>

              <Button
                variant="contained" startIcon={<Share />} onClick={handleNativeShare} fullWidth
                sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, fontSize: ".9rem", background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 16px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease", mb: 1.5 }}
              >
                Share to Story
              </Button>

              <Button
                variant="outlined" startIcon={<WhatsApp />} href={challengeWaUrl} target="_blank" fullWidth
                sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" }, mb: 1.5 }}
              >
                Challenge a Friend on WhatsApp
              </Button>

              <Button
                variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
                sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", "&:hover": { borderColor: copied ? "rgba(34,197,94,.5)" : BLUE, color: copied ? "#22C55E" : BLUE }, transition: "all .2s ease" }}
              >
                {copied ? "Link Copied!" : "Copy Question Link"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicQA;
